import { useNavigate } from 'react-router-dom';
import { useMdmContextStore } from '@/stores/mdmContextStore';
import { App } from 'antd';
import { authApi } from '@/api/authApi';
import { useAuthStore } from '@/stores/authStore';
import type { LoginRequest } from '@/types/auth';

/**
 * Hook de auth con metodos login/logout listos para usar desde componentes.
 */
export function useAuth() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { user, accessToken, isAuthenticated, setTokens, clear } = useAuthStore();

  async function login(request: LoginRequest) {
    const response = await authApi.login(request);

    if (response.requiresEnterpriseSelection) {
      return { requiresSelection: true, availableEnterprises: response.availableEnterprises };
    }

    setTokens(response.accessToken, response.refreshToken, response.user);

    // Si el usuario tiene un MDM por defecto guardado en BD, aplicarlo automaticamente
    if (response.user.defaultMdmId && response.user.defaultMdmName) {
      useMdmContextStore.getState().setMdm(
        response.user.defaultMdmId,
        response.user.defaultMdmName,
        false // no persistir de nuevo al servidor, ya viene de ahi
      );
    }

    return { requiresSelection: false };
  }

  async function selectEnterprise(enterpriseId: number) {
    const response = await authApi.selectEnterprise({ enterpriseId });
    setTokens(response.accessToken, response.refreshToken, response.user);
  }

  async function logout() {
    const refreshToken = useAuthStore.getState().refreshToken;
    try {
      if (refreshToken) {
        await authApi.logout({ refreshToken });
      }
    } catch {
      // Si falla, igual cerramos sesion local
    } finally {
      clear();
      message.success('Sesion cerrada');
      navigate('/login', { replace: true });
    }
  }

  return {
    user,
    accessToken,
    isAuthenticated,
    login,
    selectEnterprise,
    logout,
  };
}
